import React, { useCallback } from 'react';
import { Button, Tooltip, Typography } from '@rbx/ui';
import Router from 'next/router';
import useRailStyles from './Rail.styles';

type TRailItemProps = {
  icon: React.ReactNode;
  active?: boolean;
  activeIcon?: React.ReactNode;
  tooltip?: string;
  label?: string;
  bottom?: boolean;
  compact?: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  href?: string;
  disableAnimation?: boolean;
  compactStyles?: 'iconOnly' | 'vertical';
};

const RailItem: React.FC<TRailItemProps> = ({
  onClick,
  icon,
  active = false,
  activeIcon,
  tooltip,
  label,
  compact = false,
  bottom = false,
  disableAnimation = false,
  href,
  compactStyles = 'vertical',
}) => {
  const isVertical = compactStyles === 'vertical';
  const isIconOnly = compactStyles === 'iconOnly';

  const {
    cx,
    classes: {
      railItemIconOnly,
      railItem,
      railItemVertical,
      railItemBottom,
      activeRailItem,
      startIcon,
      verticalLabel,
      hidden,
      label: labelClass,
      labelTransition,
    },
  } = useRailStyles();

  const onClickWrapper: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.preventDefault();
      onClick(e);

      if (!href) {
        return;
      }

      setTimeout(() => {
        const isAbsoluteUrl = href.startsWith('http');
        if (isAbsoluteUrl) {
          window.open(href, '_self');
        } else {
          Router.push(href);
        }
      }, 100);
    },
    [href, onClick],
  );

  const textVariant = isVertical && compact ? 'captionSmall' : 'largeLabel2';
  const button = (
    <Button
      href={href}
      onClick={onClickWrapper}
      variant='text'
      classes={{
        root: cx(railItem, {
          [railItemBottom]: bottom,
          [activeRailItem]: active,
          [railItemIconOnly]: compact && isIconOnly,
          [railItemVertical]: compact && isVertical,
        }),
        startIcon: cx({ [startIcon]: true }),
      }}
      color={active ? 'secondary' : 'primary'}
      startIcon={(active && activeIcon) || icon}>
      <Typography
        classes={{
          root: cx(labelClass, {
            [labelTransition]: !disableAnimation,
            [hidden]: compact && isIconOnly,
            [verticalLabel]: compact && isVertical,
          }),
        }}
        variant={textVariant}>
        {label}
      </Typography>
    </Button>
  );

  if (isVertical) {
    return button;
  }

  return (
    <Tooltip title={compact && (tooltip ?? label)} placement='right'>
      {button}
    </Tooltip>
  );
};

export default RailItem;
